import {Component, Input} from '@angular/core';
import {PlateProcessingService} from '../plate-processing/plate-processing.service';
import {MessagesNewService} from '../messages-new/messages.service';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {SelectableLab} from '../carrier/selectable-lab';
import {Plate} from '../models-new/plate';
import {Sample} from '../models-new/sample';
import {AnnouncementService} from '../announcement/announcement.service';
import {SamplePlateHistoryDTO} from '../models-new/samplePlateHistoryDTO';
import {SampleCommentDTO} from '../models-new/sampleCommentDTO';
import {ProcessUpdateDTO, SampleData} from '../models/wb-process-update';
import {ProcessUpdateStage} from '../models/process-step';
import {WorkFlow} from '../masterdata/models/workFlow';
import prepareRemoveSampleDTO from "../utils/remove-sample-util";
import {ReceptionNewService} from '../reception-new/reception-new.service';
import {StepStatus} from "../constants/Status";
import {ButtonType} from "../constants/ButtonType";
import {ProjectWorkflow} from '../masterdata/models/projectWorkflow';
import {ProtocolMeta} from '../protocol-configuration/dto/Protocol';
import { ErrorHandler } from '../utils/error-handler';
import { MasterDataService } from '../masterdata/masterdata.service';
import { Project } from '../masterdata/models/project';


@Component({
	selector: 'app-sample-placement',
	templateUrl: './sample-placement.component.html',
	styleUrls: ['./sample-placement.component.css']
})
export class SamplePlacementComponent {

	@Input() activeProcessStep: boolean = false;
    @Input() eligiblePlateStep!:string;
	@Input() workflowMap: Map<string, ProjectWorkflow>  = new Map();

	readonly columnHeaders = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
	readonly rowHeaders = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
	private readonly BARCODE_SCANNER_ID = 'barcode-scanner';
	readonly PLATE_ID_FIELD_NAMES = ['plateID', 'pgxPlateID', 'wgaPlateID', 'lysisPlateID', 'dnaPlateID', 'magMixPlateID', 'aliquotPlateID']

	puid: string = "";
	viewType: string = "";
	samplePlacementPlateID: string = "";
	showView: boolean = false;
	scannedBarcode: string = "";
	positionBarcodeMap: Map<string, string> = new Map();
	blocked: boolean = false;
	selectedLabId: string;
	selectedLab: SelectableLab;
	samplePlacementPlate: Plate = null;
	sampleBarcodeMap: Map<string, string> = new Map();
	selectedCell: string = "A01";
	samplePlacementPlateSampleType: string = "";
	plateSamplesMap: Map<string, Sample> = new Map();
	disableSamplePlacementPlateIDField: boolean = false;
	showPlateView: boolean = false;
	isSampleSelectionOn: boolean = false;
	selectedPositions: Map<string, Sample> = new Map();
	addedSamplesInExtractedPlate = new Set();
	allSelectedSamples: any[] = [];
	duplicateSamplesPopUpNormalPlate: boolean = false;
	duplicateSamplesPresent: boolean = false;
	sampleData: Sample = new Sample();
	sampleCheckErrorMessage: string = '';
	showSampleCheckFailedDialog: boolean = false;
	existingPositions: string[] = [];
	selectedPickedSample: Sample;
	isProcessUpdateProxied:boolean;
	selectedWorkFlow : WorkFlow;
	selectedSample: Sample = new Sample();
	errorHandler: ErrorHandler = new ErrorHandler;
	projectData: Project[] = [];
	selectedProject: Project = new Project();

	constructor(
		private readonly plateProcessingService: PlateProcessingService,
		private readonly messagesService: MessagesNewService,
		private readonly responsive: BreakpointObserver,
		private readonly announcementService: AnnouncementService,
		private readonly receptionNewService: ReceptionNewService,
		private readonly masterDataService: MasterDataService
	) { }

	ngOnInit() {
		this.responsive.observe([
			Breakpoints.Large,
			Breakpoints.Medium,
			Breakpoints.Small
		]).subscribe(result => {
			const breakpoints = result.breakpoints;
			if (breakpoints[Breakpoints.Large]) {
				this.viewType = 'large';
			} else if (breakpoints[Breakpoints.Medium]) {
				this.viewType = 'medium';
			} else if (breakpoints[Breakpoints.Small]) {
				this.viewType = 'small';
			}
		});

		this.selectedLab = JSON.parse(localStorage.getItem('lab') || '{"lab":""}');
		this.selectedLabId = this.selectedLab.lab._id;
		this.puid = localStorage.getItem('PUID') || "PUID";
		this.selectedWorkFlow = JSON.parse(localStorage.getItem("selectedWorkFlow"));
		this.isProcessUpdateProxied = this.selectedWorkFlow?.proxyWeatherbyAPI;
		this.getProjectFromMDM();
	}

	getProjectFromMDM() {
		this.masterDataService.getConfigList(this.selectedLabId, "project", 0, 1000, true).subscribe({
			next: (results) => {
				this.projectData = results.data;
			},
			error: (error) => {
				this.showError(error);
			}
		})
	}

	showError(error: any) {
		const message = this.errorHandler.handleError(error);
		this.messagesService.addErrorMessage(message);
	}

	padColumnHeaders(col: number): string {
		return col.toString().padStart(2, '0');
	}

	validatePlateIsInExtractionCompleted(fetchedPlate: Plate) {
        return StepStatus.plateStepStatusMap.get(`${this.eligiblePlateStep}_${ButtonType.END}`) === fetchedPlate.status;
	}

	checkPlateForSamplePlacement() {
		this.scannedBarcode = "";

		this.positionBarcodeMap = new Map();
		this.blocked = true;
		this.plateProcessingService.getPlate(this.selectedLabId, this.samplePlacementPlateID).subscribe({
			next: (result) => {
				this.blocked = false;
                if(!this.validatePlateIsInExtractionCompleted(result)){
                    const expectedStatus = StepStatus.plateStepStatusMap.get(`${this.eligiblePlateStep}_${ButtonType.END}`);
                    this.messagesService.addErrorMessage({
                        severity:'error',summary:'Invalid plate',
                        detail:`Plate ${result.lastUpdatedPlateID} should be in ${expectedStatus} inorder to use sample placement.Please try again once the plate is in ${expectedStatus}.`
                    })
                    return;
                }
				this.samplePlacementPlate = result;
				this.selectedProject = this.projectData.find(project => project.projectId === this.samplePlacementPlate.projectID);
				this.preparePositionBarcodeMap(this.samplePlacementPlate);
				this.getSelectedCellPosition();
				this.prepareSamplesMap();
				this.samplePlacementPlateSampleType = this.extractPlateSampleType(this.samplePlacementPlate);
				this.disableSamplePlacementPlateIDField = true;
				this.showPlateView = true;

				setTimeout(() => {
					this.findAndHighlightElement(this.BARCODE_SCANNER_ID);
				}, 500)

			}, error: (_error) => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: "error", "summary": "Plate not found!",
					detail: "Plate not found with plate id - " + this.samplePlacementPlateID
				})
				this.samplePlacementPlateID = "";
			}
		})
	}

	preparePositionBarcodeMap(plate: Plate) {
		plate.samples.forEach(samp => {
			this.positionBarcodeMap.set(samp.position, samp.sampleID)
		})
	}

	getSelectedCellPosition() {
		if (this.samplePlacementPlate.scanHorizontal) {
			this.getPositionForHorizontalScan();
		}
		else {
			this.getPositionForVerticalScan();
		}
	}

	showPlacementView(){
		if(!this.activeProcessStep){
			this.messagesService.addWarningMessage({
				severity:"warn",summary:"Invalid Action",
				detail:`This plate is currently not eligible for sample placement. Please complete previous steps and try again`
				})
		}else{
			this.showView = true;
			setTimeout(() => {
				this.findAndHighlightElement('sample-placement-plate-input')
			}, 500)
		}
	}

	private getPositionForVerticalScan() {
		for (const columnHeader of this.columnHeaders) {
			for (const rowHeader of this.rowHeaders) {
				if (!this.positionBarcodeMap.has(rowHeader + this.padColumnHeaders(columnHeader))) {
					this.selectedCell = rowHeader + this.padColumnHeaders(columnHeader);
					return;
				}
			}
		}
	}

	private getPositionForHorizontalScan() {
		for (const rowHeader of this.rowHeaders) {
			for (const columnHeader of this.columnHeaders) {
				if (!this.positionBarcodeMap.has(rowHeader + this.padColumnHeaders(columnHeader))) {
					this.selectedCell = rowHeader + this.padColumnHeaders(columnHeader);
					return
				}
			}
		}
	}

	prepareSamplesMap(changeSelectedCell: boolean = true) {
		this.sampleBarcodeMap.clear();
		this.plateSamplesMap.clear();
		this.samplePlacementPlate.samples.forEach(sample => {
			this.sampleBarcodeMap.set(sample.position, sample.sampleID);
			this.plateSamplesMap.set(sample.position, sample);
		})
		if (this.samplePlacementPlate.samples.length != 0 && this.scannedBarcode != "" && changeSelectedCell)
			this.incrementSelectedCell();
	}

	incrementSelectedCell() {
		if (this.sampleBarcodeMap.size == 96) {
			this.messagesService.addSuccessMessage({
				severity: 'success', summary: 'Plate filled',
				detail: `Plate ${this.samplePlacementPlate.lastUpdatedPlateID} is completely filled.`
			});
			return;
		}
		let row = this.selectedCell[0], col = Number.parseInt(this.selectedCell.substring(1)), rowDiff = 0;

		if (this.samplePlacementPlate.scanHorizontal) {
			this.incrementSelectedCellForHorizontalScan(col, row);
		}
		else {
			this.incrementSelectedCellForVerticalScan(col, row);
		}
	}

	private incrementSelectedCellForVerticalScan(col: number, row: string) {
		while (true) {
			col = row == this.rowHeaders[7] ? col + 1 : col;
			col = col == 13 ? 1 : col;
			let rowDiff = row.charCodeAt(0) - 'A'.charCodeAt(0);
			row = this.rowHeaders[rowDiff == 7 ? 0 : rowDiff + 1];
			if (!this.sampleBarcodeMap.has(row + this.padColumnHeaders(col))) {
				this.selectedCell = row + this.padColumnHeaders(col);
				break;
			}
		}
	}

	private incrementSelectedCellForHorizontalScan(col: number, row: string) {
		let rowIndex;
		while (true) {
			rowIndex = col == this.columnHeaders[11] ? this.rowHeaders.indexOf(row) + 1 : this.rowHeaders.indexOf(row);
			row = rowIndex == 8 ? this.rowHeaders[0] : this.rowHeaders[rowIndex];
			col = col == 12 ? 1 : col + 1;
			if (!this.sampleBarcodeMap.has(row + this.padColumnHeaders(col))) {
				this.selectedCell = row + this.padColumnHeaders(col);
				break;
			}
		}
	}

	extractPlateSampleType(plate: Plate) {
		let plateSampleTypes = plate.sampleType.split(",").map(item => item.trim())
		return plateSampleTypes[plateSampleTypes.length - 1]
	}

	findAndHighlightElement(elementId) {
		let element = document.getElementById(elementId);
		if (element) {
			element.focus();
		}
	}

	closePopUp() {
		this.resetField();
		this.showView = false;
	}

	convertToDate(date: Date): Date {
		if (!date || date == null) {
			return;
		}
		return new Date(date);
	}

	createNewSample(position: string, sampleData: Sample) {
		const newSample = new Sample();
		newSample._id = sampleData._id;
		newSample.position = position;
		newSample.sampleID = sampleData.barCode;
		newSample.beadChipID = '';
		newSample.beadChipPosition = '';
		newSample.plateRedo = false;
		newSample.priority = sampleData.priority;
		newSample.sampleType = sampleData.sampleType;
		newSample.singleRedo = false;
		newSample.icbfSampleID = sampleData.icbfSampleID ? sampleData.icbfSampleID : 0;
		newSample.reRunCount = sampleData.reRunCount;
		newSample.autoRerun = sampleData.autoRerun;
		newSample.qcSampleHistory = [];
		newSample.selectedSampleType = this.samplePlacementPlateSampleType;
		newSample.boxID = sampleData.boxID;
		newSample.boxReceptionTimeStamp = this.convertToDate(sampleData.boxReceptionTimeStamp);
		newSample.projectID = sampleData.projectID;
		newSample.dueDate = sampleData.dueDate;
        newSample.receptionPUID = this.puid;
		newSample.mintSampleID = sampleData.mintSampleID;
		newSample.parentMintSampleID = sampleData.parentMintSampleID;
		newSample.parentSampleID = sampleData.parentBarcode;
		if (this.selectedProject.manualWorkflow) {
			newSample.workflowID = this.samplePlacementPlate.workflowID;
		} else {
			newSample.workflowID = sampleData.workflowID;
		}

		let gender = sampleData.gender;
		if (gender == "M") {
			newSample.gender = "male";
		} else if (gender == "F") {
			newSample.gender = "female";
		}
		else {
			newSample.gender = gender;
		}

		return newSample;
	}

	setDuplicateSamplesPopUp() {
		this.duplicateSamplesPresent = this.duplicateSamplesPopUpNormalPlate;
	}

	resetValues(event){
		this.duplicateSamplesPopUpNormalPlate = false;
		this.duplicateSamplesPresent = false;
		this.blocked = false;
		this.setDuplicateSamplesPopUp()
	}

	selectedSampleFromDuplicate(sampleObjectId: string){
		let sample = this.allSelectedSamples.find(sample => sample._id === sampleObjectId)
		this.selectedSampleFromScan(sample);
	}

	selectedSampleFromScan(sample){
		this.announcementService.putBarcodeInSample(this.scannedBarcode, sample).subscribe({
		  next: (result) => {
			this.blocked = false;
			this.selectedPickedSample = result.data
			if(this.duplicateSamplesPopUpNormalPlate){
			  this.saveBarcodeForNormalPlateAfterDuplicateSelection(this.selectedPickedSample)
			}
		  },
		 error: (error: any) => {
			this.blocked = false;
			this.messagesService.addErrorMessage({
			  severity:"error", summary:"Encountered error fetching Sample "+this.scannedBarcode,
			  detail: error
			});
		  }
		});
	}

	saveBarcodeForNormalPlateAfterDuplicateSelection(sample){
		this.sampleData = sample
		let flagSampleExists = false
		this.samplePlacementPlate.samples.forEach(res => {
		  if(res._id == sample._id){
			this.messagesService.addErrorMessage({
			  severity: 'error', summary:"Sample Not Added!",
			  detail: "Sample already exists"
			})
			flagSampleExists = true
		  }
		})
		if(flagSampleExists){
		  this.setDuplicateSamplesPopUp()
		  return
		}
		if(!this.validateSample()){
			return;
		}
		this.blocked = true;
		this.plateProcessingService.checkSamplePresentInPlate(this.selectedLabId, this.sampleData._id).subscribe({
			next: (result_value)=>{
			this.blocked = false;
			if(result_value.present){
			  this.messagesService.addWarningMessage({
				severity: 'warn', summary: 'Invalid Barcode',
				detail: "Barcode: '"+this.scannedBarcode +"' is already scanned in Plate "+ result_value.plateID +". Please scan a different barcode.",
			  });
			  this.sampleCheckErrorMessage = "Barcode: '"+this.scannedBarcode +"' is already scanned. Please scan a different barcode."
			  return;
			}
			this.checkIfCellIsEmptyAndAddSample(false, "duplicate");
		  },
		  error: error => {
			this.blocked = false;
			this.messagesService.addErrorMessage({
			  severity:"error", summary:" Sample "+this.scannedBarcode+" not Present in Storage Plate.",
			  detail: error
			})
		  }
		})
	  }

	placeRemovedNonSRCSample() {
		this.blocked = true;
		this.announcementService.getSampleFromDelivery(this.selectedLabId, this.scannedBarcode).subscribe({
			next: (result) => {
				this.blocked = false
				const samples = result.data
				this.allSelectedSamples = samples.filter(sample => sample.projectID === this.samplePlacementPlate.projectID);
				if (this.allSelectedSamples.length > 1) {
					this.duplicateSamplesPopUpNormalPlate = true
					this.setDuplicateSamplesPopUp()
				}
				else if (this.allSelectedSamples.length == 1) {
					this.sampleData = result.data
					this.sampleData = this.sampleData[0]
					if(!this.validateSample()){
						return;
					}
					this.checkIfCellIsEmptyAndAddSample(false, "redo");

					if (this.sampleData.storage) {
						this.deleteStorageSample();
					}
				}
			},
			error: (error: any) => {
				this.blocked = false;
				this.sampleCheckErrorMessage = `Sample ${this.scannedBarcode} was not announced in the manifest for project.`;
				this.showSampleCheckFailedDialog = true;
				this.scannedBarcode = "";
			}
		})
	}

	validateSample():boolean{
		if (!this.sampleData.boxReceptionTimeStamp) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Invalid Sample",
				detail: `Sample ${this.scannedBarcode} is not allowed in this plate as there is no Box Reception Information`
			})
			return false;
		}
		if (this.sampleData.receptionStatus === "REMOVED") {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Unsuitable sample scanned",
				detail: `Sample ${this.scannedBarcode} is marked as unsuitable.`
			})
			this.scannedBarcode = "";
			return false;
		}
		if (this.sampleData.storage && !this.sampleData.announced) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Storage Sample not Allowed",
				detail: `Sample ${this.sampleData.barCode} is in storage & Not announced. So, sample is not allowed to be placed on the plate.`
			})
			this.scannedBarcode = "";
			return false;
		}

		if (this.sampleData.storage && !this.sampleData.activated) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Storage Sample not Allowed",
				detail: `Sample ${this.sampleData.barCode} is in storage & Not activated. So, sample is not allowed to be placed on the plate.`
			})
			this.scannedBarcode = "";
			return false;
		}

		if(!this.sampleData.activated){
			this.sampleCheckErrorMessage = `Sample ${this.scannedBarcode} is not yet activated!`;
			this.showSampleCheckFailedDialog = true;
			return false
		}
		if (!this.sampleData.announced) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Barcode Already Registered",
				detail: `Sample ${this.scannedBarcode} is already registered in the system . Announcement yet to be received`
			})
			return false;
		}
		if (this.sampleData.projectID !== this.samplePlacementPlate.projectID) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Wrong projectID",
				detail: `Sample ${this.scannedBarcode} is not allowed in this plate.`
			})
			return false;
		}
		if (!this.checkDownStreamProtocolMatch()) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Downstream Protocol Mismatch!!",
				detail: `Sample ${this.scannedBarcode} is not allowed in this plate.`
			})
			return false;
		}

		return true;
	}

	checkDownStreamProtocolMatch(): boolean {
		const plateKey = `${this.samplePlacementPlate.projectID}_${this.samplePlacementPlate.workFlow}`;
		const plateDownStreamProtocol:ProtocolMeta = this.workflowMap.get(plateKey)?.downstream[0];

		const sampleKey = `${this.sampleData.projectID}_${this.sampleData.workFlow}`;
		const sampleDownStreamProtocol:ProtocolMeta = this.workflowMap.get(sampleKey)?.downstream[0];

		return sampleDownStreamProtocol?.protocolName === plateDownStreamProtocol?.protocolName && sampleDownStreamProtocol?.protocolVersion === plateDownStreamProtocol?.protocolVersion;
	}

	checkIfCellIsEmptyAndAddSample(storagePlate: boolean, sampleType: string = "") {
		if (!this.existingPositions.includes(this.selectedCell)) {
			if (!storagePlate) {
				if (sampleType === "") {
					return;
				}
				else {
					this.normalBarcodePlate(sampleType);
				}
			}
		}
		else {
			this.messagesService.addErrorMessage({
				severity: "error", summary: "Cell is already occupied ",
				detail: "Sample already present"
			})
			if (!storagePlate && sampleType != "") {
				this.setDuplicateSamplesPopUp();
			}
		}
	}

	saveBarcode() {
		this.isSampleSelectionOn = true;
		setTimeout(() => {
			try {
				this.checkAndPlaceSample();
			} catch (error) {
				this.scannedBarcode = "";
				throw error;
			} finally {
				this.isSampleSelectionOn = false;
			}
		});
	}

	checkAndPlaceSample() {
		this.scannedBarcode = this.scannedBarcode.trim();

		if ("" == this.scannedBarcode.trim()) {
			return;
		}

		if (!this.showPlateView) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Sample not placed!",
				detail: `PlateID ${this.samplePlacementPlateID} is not valid`
			})
			this.scannedBarcode = "";
			return;
		}

		if (this.sampleBarcodeMap.has(this.selectedCell)) {
			this.messagesService.addWarningMessage({
				severity: "warn", summary: "Sample not placed!",
				detail: `Cell ${this.selectedCell} already has a sample present`
			})
			this.scannedBarcode = "";
			return;
		}



		let requestBody = {};
		requestBody["labId"] = this.selectedLabId;
		requestBody["plateId"] = this.samplePlacementPlateID;
		requestBody["selectedSampleType"] = this.samplePlacementPlateSampleType;
		requestBody["sampleBottomBarcode"] = this.scannedBarcode;
		requestBody["position"] = this.selectedCell;
		requestBody['workflow'] = this.samplePlacementPlate.workFlow;
		requestBody['workflowID'] = this.samplePlacementPlate.workflowID;
		this.blocked = true;
		this.announcementService.placeRemovedSRCSample(requestBody).subscribe({
			next: (result) => {
				this.blocked = false;
				let sample = this.createNewSample(this.selectedCell, result.data);
				this.setLastUpdatedPlateInfo(sample);
				sample["currentPlatePriority"] = this.samplePlacementPlate.priority;
				let plateDueDate = this.samplePlacementPlate.dueDate;
				if (this.samplePlacementPlate.dueDate < sample.dueDate) {
					sample.plateDueDate = plateDueDate;
				} else {
					sample.plateDueDate = sample.dueDate;
				}
				this.blocked = true;
				this.plateProcessingService.addSampleToPlate(this.selectedLabId, this.samplePlacementPlateID, sample).subscribe({
					next: result => {
						this.blocked = false;
						this.samplePlacementPlate = result;
						this.preparePositionBarcodeMap(this.samplePlacementPlate);
						this.prepareSamplesMap();
						this.messagesService.addSuccessMessage({
							severity: "success", summary: "Sample placed!"
						})
						if (sample.icbfSampleID != 0) {
							this.addedSamplesInExtractedPlate.add(sample._id);
						}
						this.scannedBarcode = "";
					}, error: _error => {
						this.blocked = false;
						this.scannedBarcode = "";
						this.messagesService.addSuccessMessage({
							severity: "error", summary: "Sample not placed!",
							detail: "Error occured while adding sample to the plate!"
						})
					}
				});
			},
			error: (error: any) => {
				this.blocked = true;
				this.placeRemovedNonSRCSample();
			}
		})
	}

	deleteStorageSample() {
		const sampleIDList = []
		const samplePositionList = []
		samplePositionList.push(this.sampleData.plateHistory[this.sampleData.plateHistory.length - 1].position)
		sampleIDList.push(this.sampleData._id)

		const comment = new SampleCommentDTO();
		comment.comment = "sample scanned in " + this.samplePlacementPlate.plateID;
		comment.puid = this.puid;
		comment.status = "SAMPLE_RECEPTION";
		const body = {
			"samplePositionList": samplePositionList,
			"labID": this.selectedLabId,
			"plateID": this.sampleData.plateHistory[this.sampleData.plateHistory.length - 1].plateId,
			"comment": comment
		}
		this.blocked = true;
		this.receptionNewService.removeSamples(body).subscribe({
			next: (resultSamples) => {
				this.blocked = false;
				if (resultSamples.includes("Samples deleted successfully")) {
					this.messagesService.addInfoMessage({
						severity: 'info', summary: 'Sample in storage',
						detail: `Sample ${this.scannedBarcode} removed from the storage plate`,
					})
					body["sampleList"] = sampleIDList;
					body["resetProcessingStartDate"] = !this.samplePlacementPlate.storagePlate
					this.blocked = true;
					this.announcementService.removeSamples(body).subscribe({
						next: (resultDelivery) => {
							this.blocked = false;
						},
						error: (_error) => {
							this.blocked = false;
						}
					})
				}
			},
			error: (error) => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: 'error', summary: 'Error',
					detail: "Sample was not deleted"
				})
			}
		})
	}

	resetField() {
		this.samplePlacementPlateID = "";
		this.disableSamplePlacementPlateIDField = false;
		this.showPlateView = false;
		this.scannedBarcode = "";
		this.selectedCell = "A01";
		this.isSampleSelectionOn = false;
		this.selectedPositions.clear();
		this.performProcessUpdateForWeatherby(new Array(...this.addedSamplesInExtractedPlate.values()));
		setTimeout(() => {
			this.findAndHighlightElement('sample-placement-plate-input')
		}, 200)
	}

	addSampleToPlateForDeliveryService(sample_id: string, sample_barcode: string, addComment: boolean, addPlateHistory: boolean, addReceptionValues:boolean){
		let data = {}
		data['addComment'] = addComment;
		data['addPlateHistory'] = addPlateHistory;
		if(!('receptionDateTime' in this.sampleData) || this.sampleData.receptionDateTime == null){
		  data['addReceptionValues'] = addReceptionValues;
		}
		else{
		  data['addReceptionValues'] = false
		}

		let plateSampleTypes = this.samplePlacementPlate.sampleType.split(",");
		let latestPlateSampleType = plateSampleTypes[plateSampleTypes.length-1];
		if(latestPlateSampleType!= this.sampleData.sampleType){
		  data['addComment'] = true;
		  this.sampleCheckErrorMessage =  "Sample was received as "+ this.samplePlacementPlateSampleType;
		}
		data['selectedSampleType'] = latestPlateSampleType;
		let comment = new SampleCommentDTO();
		comment.comment = this.sampleCheckErrorMessage;
		comment.puid = this.puid;
		comment.status = "SAMPLE_RECEPTION";
		data['comment'] = comment;

		let plateHistory = new SamplePlateHistoryDTO();
		plateHistory.plateId = this.samplePlacementPlateID
		plateHistory.position = this.selectedCell;
		plateHistory.approved = true;

		data['plateHistory']=plateHistory;
		data['puid'] = this.puid;
		data['boxID'] = !this.sampleData.boxID ? this.samplePlacementPlate.delivery : this.sampleData.boxID;

		data['status'] = "UNBOXED";
		data['storage'] = this.samplePlacementPlate.storagePlate;
		data['sampleType'] = this.sampleData.sampleType
		data['projectID']=this.sampleData.projectID;
		data['workflow'] = this.samplePlacementPlate.workFlow;
		data['workflowID'] = this.samplePlacementPlate.workflowID;

		this.blocked = true;
		this.announcementService.addSampleToPlate(this.selectedLabId, sample_id, data).subscribe({
			next: result => {
			this.blocked = false;
			if(result.data != null){
			  plateHistory.sampleId = sample_barcode;
			  plateHistory.id = sample_id
			  this.addedSamplesInExtractedPlate.delete(sample_id);
			}
		  },
		  error: error => {
			this.blocked = false;
			this.messagesService.addErrorMessage({
			  severity: 'error', summary:"Sample Update Failed",
			  detail: "Sample update for plate history and comment failed!\n"+error
			})
			this.scannedBarcode = '';
		  }
		})
	  }

	normalBarcodePlate(text: string){
		this.blocked = true;
		this.plateProcessingService.checkSamplePresentInPlate(this.selectedLabId, this.sampleData._id).subscribe({
			next: (result_value) => {
				this.blocked = false;
				if (result_value.present) {
					this.messagesService.addWarningMessage({
						severity: 'warn', summary: 'Invalid Barcode',
						detail: "Barcode: '" + this.scannedBarcode + "' is already scanned in Plate " + result_value.plateID + ". Please scan a different barcode.",
					});
					this.sampleCheckErrorMessage = "Barcode: '" + this.scannedBarcode + "' is already scanned. Please scan a different barcode."
					this.scannedBarcode = "";
					return;
				}
				if (!this.sampleData.activated) {
					this.sampleCheckErrorMessage = "Sample " + this.scannedBarcode + " is not yet activated!"
					this.scannedBarcode = "";
					this.showSampleCheckFailedDialog = true;
				}
				else {
					let newSample = this.createNewSample(this.selectedCell, this.sampleData);
					this.setLastUpdatedPlateInfo(newSample)
					newSample.isRedoSample = result_value.redo;
					let plateDueDate = this.samplePlacementPlate.dueDate;
					if (this.samplePlacementPlate.dueDate < newSample.dueDate) {
						newSample.plateDueDate = plateDueDate;
					} else {
						newSample.plateDueDate = newSample.dueDate;
					}
					this.addSampleToPlateForDeliveryService(this.sampleData._id, this.scannedBarcode, false, true, false);
					this.blocked = true;
					this.plateProcessingService.addSampleToPlate(this.selectedLabId, this.samplePlacementPlate.plateID, newSample).subscribe({
						next: result => {
							if(this.sampleData.icbfSampleID) this.addedSamplesInExtractedPlate.add(this.sampleData._id);
							this.plateProcessingService.updateReProcessedToSample(this.selectedLabId, this.sampleData._id).subscribe({
								next: (_result) => {
									this.blocked = false;
								},
								error: error => {
									this.blocked = false;
									this.messagesService.addErrorMessage({
										severity: "error", summary: " Error occured in re processing a sample " + this.scannedBarcode,
										detail: error.error
									})
									this.scannedBarcode = "";
								}
							})
							if (text == 'duplicate') {
								this.duplicateSamplesPopUpNormalPlate = false;
								this.setDuplicateSamplesPopUp()
							}
							this.scannedBarcode = '';
							this.samplePlacementPlate = result;
							this.sampleBarcodeMap.set(this.selectedCell, this.scannedBarcode);
							this.plateSamplesMap.set(this.selectedCell, newSample);
							if (this.samplePlacementPlate.samples.length == 96) {
								this.selectedCell = "A01"
							}
							else {
								this.incrementSelectedCell();
							}
							this.messagesService.addSuccessMessage({
								severity: 'success', summary: "Sample Added!",
								detail: "Sample " + this.scannedBarcode + " added to plate " + this.samplePlacementPlate.plateID
							});
							this.scannedBarcode = '';
						},
						error: error => {
							this.blocked = false;
							this.messagesService.addErrorMessage({
								severity: 'error', summary: "Sample Not Added!",
								detail: error
							})
							this.scannedBarcode = '';
						}
					});
					this.findAndHighlightElement(this.BARCODE_SCANNER_ID);
				}
			},
			error: error => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: "error", summary: " Sample " + this.scannedBarcode + " not Present in Storage Plate.",
					detail: error
				})
				this.scannedBarcode = "";
			}
		})

	}

	closeSampleCheckFailedDialog(){
		this.showSampleCheckFailedDialog = false;
		this.findAndHighlightElement(this.BARCODE_SCANNER_ID);
	}

	turnOnSampleSelection() {
		this.isSampleSelectionOn = true;
		this.selectedCell = "";
		this.selectedPositions.clear();
	}

	turnOffSampleSelection() {
		this.isSampleSelectionOn = false;
		this.selectedPositions.clear();
		this.getSelectedCellPosition();
	}

	disableRemoveSamplesButton(){
		return (this.selectedPositions.size == 0)
	}

	setSelectedGridCell(data: string){
		this.selectedCell = data;
		setTimeout(() => {
			this.findAndHighlightElement(this.BARCODE_SCANNER_ID)
		}, 500)
	}

	setSelectedGridCells(data: Array<{position: string, sampleId: string}>){
		this.selectedPositions.clear();
		data.forEach(cell => {
			this.selectedPositions.set(cell.position, this.plateSamplesMap.get(cell.position))
		})
	}

	removeSamples() {
        const samplePositionList = Array.from(this.selectedPositions.keys());
		const body = prepareRemoveSampleDTO(
			this.samplePlacementPlate,
			this.selectedLabId,
			samplePositionList,
			this.puid,
			this.sampleBarcodeMap
		)
		this.blocked = true;
		this.plateProcessingService.removeSamples(body).subscribe({
			next: _res_samples => {
				this.blocked = false;
				let sampleIDList = [];
				this.selectedPositions.forEach((value, _key) => {
					sampleIDList.push(value._id);
					this.addedSamplesInExtractedPlate.delete(value._id);
				})
				body["sampleList"] = sampleIDList;
				this.blocked = true;
				this.announcementService.removeSamples(body).subscribe({
					next: _res_delivery => {
						this.blocked = false;
						this.isSampleSelectionOn = false;
						this.selectedPositions.clear();
						this.messagesService.addSuccessMessage({
							severity: 'success', summary: 'Sample(s) removed from plate!',
						})
						this.checkPlateForSamplePlacement();

					},
					error: error => {
						this.blocked = false;
						this.messagesService.addErrorMessage({
							severity: "error", summary: "Error occured!",
							detail: error.bbApiError.message
						})
					}
				})
			},
			error: (_error) => {
				this.blocked = false;
				this.messagesService.addErrorMessage({
					severity: 'error', summary: 'Error',
					detail: "Samples were not removed!"
				})
			}
		})
	}

	performProcessUpdateForWeatherby(samples: Array<any>) {
		let weatherbySampleDataList = samples.map<SampleData>(sample => { return {
			sampleId:sample,
			description: "",
			stageGate : 3,
			status: ProcessUpdateStage.STAGE_GATE
		}})

		if(weatherbySampleDataList.length == 0){
			return;
		}

		let weatherByProcessUpdateDto: ProcessUpdateDTO = {
			data: weatherbySampleDataList,
			labId: this.selectedLabId
		}
		this.announcementService.weatherbyProcessUpdate(weatherByProcessUpdateDto, this.isProcessUpdateProxied).subscribe({
			next: (_result) => {
				this.addedSamplesInExtractedPlate.clear();
			},
			error: (error) => {
				this.messagesService.addErrorMessage({
					severity: 'error', summary: 'Something went wrong',
					detail: `Error occurred while sending process update to weatherbys.Error: ${error.bbApiError.message} `
				})
				this.addedSamplesInExtractedPlate.clear();
			}
		})
	}

	private setLastUpdatedPlateInfo(sample: Sample) {
		sample.lastUpdatedPlateFieldValue = this.samplePlacementPlate.lastUpdatedPlateID;
		for (const property of this.PLATE_ID_FIELD_NAMES) {
			if (this.samplePlacementPlate[property] === sample.lastUpdatedPlateFieldValue) {
				sample.lastUpdatedPlateFieldName = property
				break;
			}
		}
	}
}
